import { memo, type ReactNode, type SyntheticEvent } from 'react'
import { motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'

import {
  ACTION_HEADER_ICON_CENTER_CLASS,
  ACTION_HEADER_INDENT_CLASS,
} from '@renderer/overlay/Sidepanel/components/action-header'
import { ActionSendingBadge, ActionSuccessBadge } from '@renderer/overlay/Sidepanel/components/action-status-badge'
import { useTrackStagedActionShown } from '@renderer/overlay/Sidepanel/hooks/staged-actions/use-track-staged-action-shown'
import { ActionViewBody } from '@renderer/overlay/Sidepanel/views/action-view/nested/body'
import { ActionViewInlineControls } from '@renderer/overlay/Sidepanel/views/action-view/nested/floating-controls'
import { ActionViewHeader } from '@renderer/overlay/Sidepanel/views/action-view/nested/header'
import { useActionViewModel } from '@renderer/overlay/Sidepanel/views/action-view/use-action-view-model'
import { useDraftAction, type ActionDraftControl } from '@renderer/overlay/Sidepanel/views/action-view/use-draft-action'
import {
  useActionLock,
  useIsActionHeld,
  type ActionLock,
} from '@renderer/overlay/Sidepanel/views/action-view/use-action-lock'
import { CreditsLockedPage } from '@renderer/overlay/Sidepanel/components/sidepanel/plan-banners/credits-lock'
import { LockedActionBody } from '@renderer/overlay/Sidepanel/views/action-view/nested/locked-action-body'
import { OutputCaption, OutputMeetingSource } from './caption-pill'

interface StagedActionOutputProps {
  actionId: string
  label: string
  caption: string | undefined
  focused: boolean
  animateHeight: boolean
  hotkeysEnabled: boolean
  connectToNext: boolean
  standalone: boolean
  meetingAudioNoteId: string | undefined
  /** Under the title, as the meeting source sits. A meeting source takes the slot when both are set. */
  annotation?: ReactNode
  hideContent: boolean
  threadOpen: boolean
  onFocus: () => void
  onOpenThread: () => void
}

// Delayed visibility preserves the fade, then removes collapsed controls from keyboard and accessibility navigation.
const focusedControlsVariants: Variants = {
  collapsed: { height: 0, opacity: 0, visibility: 'hidden', overflow: 'hidden' },
  collapsedAfterFade: {
    opacity: 0,
    overflow: 'hidden',
    transitionEnd: { height: 0, visibility: 'hidden' },
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    visibility: 'visible',
    overflow: 'hidden',
    transitionEnd: { overflow: 'visible' },
  },
}

const focusedControlsTransition: Transition = { duration: 0.2, ease: 'easeOut' }
const focusedControlsFadeTransition: Transition = {
  duration: 0.2,
  ease: 'easeOut',
  height: { duration: 0 },
}

export function StagedActionOutput({
  actionId,
  label,
  caption,
  focused,
  animateHeight,
  hotkeysEnabled,
  connectToNext,
  standalone,
  meetingAudioNoteId,
  annotation,
  hideContent,
  threadOpen,
  onFocus,
  onOpenThread,
}: StagedActionOutputProps) {
  const model = useActionViewModel(actionId)
  const lock = useActionLock(model.action)
  // A locked action isn't drafted until the user unblurs it or the credits come back.
  const isHeld = useIsActionHeld(model.action)
  const draftControl = useDraftAction(hideContent || isHeld || model.status === 'archived' ? undefined : model.action)
  // A collapsed archived row or a hidden body shows nothing of the action itself.
  useTrackStagedActionShown(hideContent || model.status === 'archived' ? undefined : model.action, 'chat-output')
  const isCompleted = model.state === 'sent'
  const showCaption = Boolean(caption) && Boolean(model.action) && model.status !== 'archived' && !hideContent
  const isFocused = focused && !isCompleted
  // Every card shows its controls; focus only decides which one owns the hotkeys.
  const controlsExpanded = !model.isDirty
  const enableHotkeys = isFocused && hotkeysEnabled && !model.isDirty
  // While a dirty row collapses, the chips must ride the fade with the rest of
  // the controls rather than pop out of the buttons mid-animation. Only the
  // binding releases synchronously; a collapsed row ends visibility:hidden, so
  // the held chips are never seen stale.
  const showHotkeys = enableHotkeys || !controlsExpanded

  const handleEditBoxInteraction = (event: SyntheticEvent<HTMLElement>) => {
    // The expand toggle skips focus, which would lift the card's cap mid-click.
    if (
      !isCompleted &&
      event.target instanceof Element &&
      event.target.closest('[data-staged-action-edit-box]') &&
      !event.target.closest('[data-card-expand-toggle]')
    ) {
      onFocus()
    }
  }

  return (
    <div className="relative">
      {connectToNext && (
        <div
          aria-hidden="true"
          className={twMerge(
            'pointer-events-none absolute -bottom-3 top-8 border-l border-dashed',
            ACTION_HEADER_ICON_CENTER_CLASS,
          )}
        />
      )}
      <section
        aria-label={label}
        data-staged-action=""
        data-focused={isFocused ? '' : undefined}
        data-completed={isCompleted ? '' : undefined}
        onPointerDownCapture={handleEditBoxInteraction}
        onFocusCapture={handleEditBoxInteraction}
        className="group/action group/staged relative"
      >
        <StagedActionContent
          model={model}
          standalone={standalone}
          meetingAudioNoteId={meetingAudioNoteId}
          annotation={annotation}
          showCompletionBadge={isCompleted}
          hideContent={hideContent}
          allowEditing={isFocused}
          draftControl={draftControl}
          lock={lock}
        />
        {/* Keep controls mounted through collapse so the exit animation finishes and hotkeys transfer synchronously.
            Dirty drafts release these controls to the floating save bar. */}
        {!standalone && !hideContent && (
          <motion.div
            initial={false}
            variants={focusedControlsVariants}
            animate={controlsExpanded ? 'expanded' : animateHeight ? 'collapsed' : 'collapsedAfterFade'}
            transition={animateHeight ? focusedControlsTransition : focusedControlsFadeTransition}
            data-staged-action-controls=""
          >
            <FocusedActionControls
              model={model}
              enableHotkeys={enableHotkeys}
              showHotkeys={showHotkeys}
              completed={isCompleted}
            />
          </motion.div>
        )}
      </section>
      {showCaption && caption && (
        <OutputCaption text={caption} standalone={standalone} threadOpen={threadOpen} onOpenThread={onOpenThread} />
      )}
    </div>
  )
}

const StagedActionContent = memo(function StagedActionContent({
  model,
  standalone,
  meetingAudioNoteId,
  annotation,
  showCompletionBadge,
  hideContent,
  allowEditing,
  draftControl,
  lock,
}: {
  model: ReturnType<typeof useActionViewModel>
  standalone: boolean
  meetingAudioNoteId: string | undefined
  annotation: ReactNode
  showCompletionBadge: boolean
  hideContent: boolean
  allowEditing: boolean
  draftControl: ActionDraftControl
  lock: ActionLock | null
}) {
  if (!model.action) {
    return model.isLoading ? <p className="text-foreground-secondary type-ui-sm px-4 py-6">Loading action…</p> : null
  }

  const isArchived = model.status === 'archived'
  const outputAnnotation = meetingAudioNoteId ? <OutputMeetingSource audioNoteId={meetingAudioNoteId} /> : annotation

  // The header goes inside the locked page so it sits in the blur's clear band, read-only like the rest.
  if (lock && !isArchived && !hideContent) {
    const header = (
      <ActionViewHeader
        model={model}
        showSource={false}
        annotation={outputAnnotation}
        allowEditing={false}
        className="py-2"
      />
    )
    return (
      <div className={twMerge('flex flex-col', standalone ? 'min-h-[520px]' : 'h-[400px]')}>
        {lock.kind === 'credits' ? (
          <CreditsLockedPage lock={lock.lock} from="chat output">
            {header}
            <ActionViewBody model={model} embedded={standalone ? 'standalone' : 'card'} />
          </CreditsLockedPage>
        ) : (
          <LockedActionBody action={model.action} tier={lock.tier} header={header} />
        )}
      </div>
    )
  }

  return (
    <>
      <ActionViewHeader
        model={model}
        showSource={false}
        annotation={outputAnnotation}
        allowEditing={allowEditing}
        isArchived={isArchived}
        subIcon={
          model.isInvoking || model.status === 'invoking' ? (
            <ActionSendingBadge />
          ) : showCompletionBadge ? (
            <ActionSuccessBadge />
          ) : undefined
        }
        className="py-2"
      />
      {!isArchived && !hideContent && (
        <ActionViewBody model={model} embedded={standalone ? 'standalone' : 'card'} draftControl={draftControl} />
      )}
    </>
  )
})

function FocusedActionControls({
  model,
  enableHotkeys,
  showHotkeys,
  completed,
}: {
  model: ReturnType<typeof useActionViewModel>
  enableHotkeys: boolean
  showHotkeys: boolean
  completed: boolean
}) {
  if (!model.action) {
    return null
  }

  return (
    <div className={twMerge('pb-3', ACTION_HEADER_INDENT_CLASS)}>
      <ActionViewInlineControls
        model={model}
        enableHotkeys={enableHotkeys}
        showHotkeys={showHotkeys}
        minimalSentControls={completed}
      />
    </div>
  )
}
